/*
 * /sonar-depth-map — the «Try it on an example» recording.
 *
 * A MODELLED lake, not a real one: a 640 × 420 m bowl with a hump, a pit, a
 * steep northern bank and a shallow bay, surveyed by lawnmower passes 28 m
 * apart and a loop near the shore. The survey is written as a real Lowrance
 * .sl2 log — 8 pings a second carrying a GPS fix that refreshes once a second,
 * depth in feet, mercator-metre positions, a few fish echoes and false-bottom
 * doubles — so the example goes through the same reader, cleaning and
 * gridding as a visitor's own log. It sits in the open Atlantic (0.4° S,
 * 24.6° W): no lake there, so nobody can take it for a real chart.
 *
 * The tests use the same writer to make their fixtures (no third-party logs
 * in the repository).
 */
(function (root) {
    'use strict';

    var NAVICO_R = 6356752.3142;
    var FT = 0.3048;
    var ORIGIN = { lat: -0.4, lon: -24.6 };
    var A = 320, B = 210;   // half-axes of the shoreline ellipse, m

    function rng(seed) {
        var s = seed >>> 0;
        return function () {
            s = (s + 0x6D2B79F5) >>> 0;
            var t = s;
            t = Math.imul(t ^ (t >>> 15), t | 1);
            t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
            return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
        };
    }

    // True depth (m) at local metres (x east, y north) — 0 outside the shore.
    function depthAt(x, y) {
        var r2 = (x / A) * (x / A) + (y / B) * (y / B);
        if (r2 >= 1) return 0;
        var d = 9.0 * Math.pow(1 - r2, 0.75);
        // steep northern bank: deeper close to the shore
        if (y > 0) d += 2.2 * Math.min(1, y / (B * 0.55)) * Math.pow(1 - r2, 0.35);
        // hump (a bar) rising to ~3 m
        var hx = x - 110, hy = y + 55;
        d -= 5.2 * Math.exp(-(hx * hx + hy * hy) / (2 * 34 * 34));
        // pit to ~13 m
        var px = x + 140, py = y - 35;
        d += 4.0 * Math.exp(-(px * px + py * py) / (2 * 16 * 16));
        // shallow bay in the east
        if (x > 200) d *= 1 - 0.55 * Math.min(1, (x - 200) / 90);
        return Math.max(0.35, d);
    }

    function toLonLat(x, y) {
        var kLat = 110574, kLon = 111320 * Math.cos(ORIGIN.lat * Math.PI / 180);
        return [ORIGIN.lon + x / kLon, ORIGIN.lat + y / kLat];
    }
    function mercX(lon) { return Math.round(lon * Math.PI / 180 * NAVICO_R); }
    function mercY(lat) { return Math.round(NAVICO_R * Math.log(Math.tan(Math.PI / 4 + lat * Math.PI / 360))); }

    // Boat path: [[x, y], …] waypoints → pings.
    function survey(opts) {
        opts = opts || {};
        var spacing = opts.spacing || 28, speed = opts.speed || 2.4, hz = opts.hz || 8;
        var way = [];
        var dir = 1;
        for (var y = -B + spacing * 0.6; y < B - spacing * 0.4; y += spacing) {
            var half = A * Math.sqrt(Math.max(0, 1 - (y / B) * (y / B))) * 0.94;
            if (half < 25) continue;
            if (dir > 0) { way.push([-half, y]); way.push([half, y]); } else { way.push([half, y]); way.push([-half, y]); }
            dir = -dir;
        }
        // loop near the shore
        for (var k = 0; k <= 72; k++) {
            var a = k / 72 * 2 * Math.PI;
            way.push([Math.cos(a) * A * 0.9, Math.sin(a) * B * 0.88]);
        }
        var pings = [], t = 0, dtMs = 1000 / hz;
        for (var w = 0; w + 1 < way.length; w++) {
            var x0 = way[w][0], y0 = way[w][1], x1 = way[w + 1][0], y1 = way[w + 1][1];
            var len = Math.hypot(x1 - x0, y1 - y0), n = Math.max(1, Math.round(len / speed * hz));
            var course = Math.atan2(x1 - x0, y1 - y0);
            for (var i = 0; i < n; i++) {
                var f = i / n;
                pings.push({ x: x0 + (x1 - x0) * f, y: y0 + (y1 - y0) * f, t: t, course: course });
                t += dtMs;
            }
        }
        return pings;
    }

    // Writes the survey as a Lowrance .sl2 log. Returns {bytes, truth}.
    function sl2(opts) {
        opts = opts || {};
        var rand = rng(opts.seed || 20261021);
        var pings = survey(opts);
        var channels = opts.channels || [0];
        // a real unit stores ~3 KB of echo samples behind every header; the
        // example keeps 16 bytes (tests ask for real sizes with opts.payload)
        var FRAME = 144, PAYLOAD = opts.payload || 16, size = FRAME + PAYLOAD;
        var total = 8 + pings.length * channels.length * size;
        var buf = new ArrayBuffer(total), dv = new DataView(buf);
        dv.setUint16(0, 2, true); dv.setUint16(2, 0, true); dv.setUint16(4, 3200, true); dv.setUint8(6, 0); dv.setUint8(7, 0);
        var pos = 8, idx = 0, fixX = null, fixY = null, lastFixT = -1e9;
        var hw = opts.hardwareTime || 1729500000;
        var truth = { lon: [], lat: [], depth: [], t: [] };
        var gpsNoise = opts.gpsNoise == null ? 1.2 : opts.gpsNoise;
        var noise = opts.depthNoise == null ? 0.05 : opts.depthNoise;
        for (var p = 0; p < pings.length; p++) {
            var pg = pings[p];
            var newFix = pg.t - lastFixT >= 1000;
            if (newFix) {
                var ll = toLonLat(pg.x + (rand() - 0.5) * 2 * gpsNoise, pg.y + (rand() - 0.5) * 2 * gpsNoise);
                fixX = mercX(ll[0]); fixY = mercY(ll[1]); lastFixT = pg.t;
            }
            var dTrue = depthAt(pg.x, pg.y);
            var d = dTrue + (rand() - 0.5) * 2 * noise;
            var u = rand();
            if (u < (opts.fish == null ? 0.006 : opts.fish)) d = Math.max(0.4, d - 1 - rand() * 2.5);
            else if (u < 0.008) d = d * 2;
            var tll = toLonLat(pg.x, pg.y);
            truth.lon.push(tll[0]); truth.lat.push(tll[1]); truth.depth.push(dTrue); truth.t.push(Math.round(pg.t));
            for (var c = 0; c < channels.length; c++) {
                dv.setUint32(pos, pos, true);
                dv.setUint16(pos + 28, size, true);
                dv.setUint16(pos + 30, size, true);
                dv.setUint16(pos + 32, channels[c], true);
                dv.setUint16(pos + 34, PAYLOAD, true);
                dv.setUint32(pos + 36, idx++, true);
                dv.setFloat32(pos + 40, 0, true);
                dv.setFloat32(pos + 44, 60, true);
                dv.setUint32(pos + 60, hw, true);
                dv.setFloat32(pos + 64, d / FT, true);
                dv.setFloat32(pos + 100, 2.4 / 0.5144, true);
                dv.setFloat32(pos + 104, 14.5, true);
                dv.setInt32(pos + 108, fixX, true);
                dv.setInt32(pos + 112, fixY, true);
                dv.setFloat32(pos + 120, pg.course, true);
                dv.setUint16(pos + 132, (newFix ? 0x0010 : 0) | 0x0002 | 0x0080, true);
                dv.setUint32(pos + 140, Math.round(pg.t), true);
                pos += size;
            }
        }
        return { bytes: new Uint8Array(buf), truth: truth };
    }

    var API = { sl2: sl2, depthAt: depthAt, toLonLat: toLonLat, survey: survey, origin: ORIGIN, axes: [A, B], rng: rng, mercX: mercX, mercY: mercY };
    root.SDMDemo = API;
    if (typeof module !== 'undefined' && module.exports) module.exports = API;
})(typeof self !== 'undefined' ? self : globalThis);
